import {
  ChartContainer,
  BarChart,
  type ChartConfig,
  type BarChartDataset,
} from "~/react/ui/chart"

type Props = {
  data?: Array<{
    date: string
    count?: number
    value?: number
    capacity?: number
  }>
  dailyLimit?: number
}

const getColors = () => {
  const root = document.documentElement
  const getVar = (name: string) =>
    getComputedStyle(root).getPropertyValue(name).trim()

  return {
    secondary200: getVar("--colors-secondary-200"),
    secondary600: getVar("--colors-secondary-600"),
    warning200: getVar("--colors-warning-200"),
    warning500: getVar("--colors-warning-500"),
    grey200: getVar("--colors-grey-200"),
  }
}

const formatDate = (date: string) => {
  const dateObj = new Date(date)
  return dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export default function EmailAccountSevenDayChart({
  data,
  dailyLimit = 20,
}: Props) {
  if (!data || data.length === 0) {
    return (
      <div
        className="email-account-7day-history-chart"
        style={{ height: 140 }}
      />
    )
  }

  const colors = getColors()

  // Transform data for chart - use per-day capacity if available, fallback to dailyLimit
  const chartData = data.map((d) => {
    const sent = d.count ?? d.value ?? 0
    const capacity = d.capacity ?? dailyLimit
    const isOverLimit = sent >= capacity
    return {
      date: formatDate(d.date),
      sent,
      capacity,
      remaining: Math.max(capacity - sent, 0),
      isOverLimit,
    }
  })

  // Create per-bar colors based on threshold
  const sentBackgroundColors = chartData.map((d) =>
    d.isOverLimit ? colors.warning200 : colors.secondary200,
  )
  const sentBorderColors = chartData.map((d) =>
    d.isOverLimit ? colors.warning500 : colors.secondary600,
  )

  const chartConfig: ChartConfig = {
    sent: {
      label: "Sent",
      color: colors.secondary200,
    },
    remaining: {
      label: "Remaining",
      color: colors.grey200,
    },
  }

  const datasets: BarChartDataset[] = [
    {
      dataKey: "sent",
      stackId: "usage",
      fill: sentBackgroundColors,
      borderColor: sentBorderColors,
      borderWidth: { top: 1, right: 0, bottom: 0, left: 0 },
      radius: { topLeft: 2, topRight: 2, bottomLeft: 0, bottomRight: 0 },
      order: 1,
    },
    {
      dataKey: "remaining",
      stackId: "usage",
      radius: { topLeft: 2, topRight: 2, bottomLeft: 0, bottomRight: 0 },
      order: 2,
    },
  ]

  // Use maximum capacity across all days for consistent y-axis scale
  const maxCapacity = Math.max(...chartData.map((d) => d.capacity), dailyLimit)

  return (
    <ChartContainer
      config={chartConfig}
      className="email-account-7day-history-chart"
    >
      <BarChart
        data={chartData}
        xAxisKey="date"
        datasets={datasets}
        stacked
        yAxisMax={maxCapacity}
        showTooltip
      />
    </ChartContainer>
  )
}
